import axios from "axios";
import { Logger } from "@nestjs/common";

import {
  IPageableRechargePaymentMethodsResponse,
  IRechargeCreatePaymentMethodRequestBody,
  IRechargePaymentMethodGetResponse,
  IRechargePaymentMethod,
  IRechargePaymentMethodGetParams,
  IRechargeUpdatePaymentMethodRequestBody,
} from "../../model/RechargePaymentMethod";
import { RechargeResourceEndpoint } from "../../RechargeEndpoint";
import { RechargeAccessTokenManager } from "../RechargeAccessTokenManager";

export class RechargePaymentMethodsClient extends RechargeResourceEndpoint<
IRechargePaymentMethodGetResponse,
IPageableRechargePaymentMethodsResponse,
IRechargePaymentMethodGetParams,
IRechargePaymentMethod
> {

  public constructor(
    rechargeKeyManager: RechargeAccessTokenManager,
    baseUrl = "https://api.rechargeapps.com",
  ) {
    super(
      axios.create({ baseURL: baseUrl }),
      rechargeKeyManager.getNextAccessToken.bind(rechargeKeyManager),
      "/payment_methods",
      new Logger(RechargePaymentMethodsClient.name),
    );
  }

  protected override singleGetter(t: IRechargePaymentMethodGetResponse): IRechargePaymentMethod {
    return t.payment_method;
  }

  protected override multiGetter(t: IPageableRechargePaymentMethodsResponse): IRechargePaymentMethod[] {
    return t.payment_methods;
  }

  public async createPaymentMethod(body: IRechargeCreatePaymentMethodRequestBody):
  Promise<IRechargePaymentMethod> {
    const response =
      await this.requestWithRetry<IRechargePaymentMethodGetResponse, IRechargeCreatePaymentMethodRequestBody>(
        {
          url: `${this.resourcePath}`,
          config: {
            headers: this.getHeaders(),
            method: "POST",
            data: body,
          },
          newKeyOnRetry: true,
          retryGatewayTimeout: false,
          retryConflict: false,
          truncateLongBody: false,
        },
      );

    return response.data.payment_method;
  }

  public async updatePaymentMethod(
    rechargePaymentMethodId: number | bigint,
    body: IRechargeUpdatePaymentMethodRequestBody,
  ): Promise<IRechargePaymentMethod> {
    const response =
      await this.requestWithRetry<IRechargePaymentMethodGetResponse, IRechargeUpdatePaymentMethodRequestBody>(
        {
          url: `${this.resourcePath}/${rechargePaymentMethodId}`,
          config: {
            headers: this.getHeaders(),
            method: "PUT",
            data: body,
          },
          newKeyOnRetry: true,
          retryGatewayTimeout: false,
          retryConflict: false,
          truncateLongBody: false,
        },
      );

    return response.data.payment_method;

  }

  public async deletePaymentMethod(rechargePaymentMethodId: number | bigint): Promise<Record<string, never>> {
    const response = await this.requestWithRetry<Record<string, never>, never>(
      {
        url: `${this.resourcePath}/${rechargePaymentMethodId}`,
        config: {
          headers: this.getHeaders(),
          method: "DELETE",
        },
        newKeyOnRetry: true,
        retryGatewayTimeout: true,
        retryConflict: true,
        truncateLongBody: false,
      },
    );
    return response.data;
  }

}
